import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { parseDetailId } from '../utils/detailRoute';

export interface DetailRoute {
    /** ID aus der URL, oder `null` in der reinen Listenansicht. */
    detailId: number | null;
    /** `true`, wenn die URL einen Parameter trägt, der keine gültige ID ist. */
    hasInvalidId: boolean;
    /** Detailansicht öffnen — erzeugt einen Verlaufseintrag (Zurück schließt sie). */
    openDetail: (id: number) => void;
    /** Detailansicht schließen — landet immer auf der Übersicht. */
    closeDetail: () => void;
}

/**
 * Verbindet eine Detail-Route (`<basePath>/:id`) mit dem Dialog einer Übersichtsseite.
 *
 * `closeDetail` navigiert bewusst **absolut** auf `basePath` statt `navigate(-1)`:
 * Wer den Link aus einer Mail öffnet, hat keinen App-Verlauf hinter sich und würde
 * mit `-1` die Anwendung verlassen. `replace` verhindert dabei, dass sich beim
 * Auf- und Zuklappen Verlaufseinträge stapeln.
 */
export function useDetailRoute(basePath: string): DetailRoute {
    const { id } = useParams<{ id?: string }>();
    const navigate = useNavigate();

    const detailId = parseDetailId(id);

    const openDetail = useCallback(
        (nextId: number) => navigate(`${basePath}/${nextId}`),
        [basePath, navigate],
    );

    const closeDetail = useCallback(
        () => navigate(basePath, { replace: true }),
        [basePath, navigate],
    );

    return {
        detailId,
        hasInvalidId: id !== undefined && detailId === null,
        openDetail,
        closeDetail,
    };
}
